import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import {
  CircleHelp,
  CheckCircle2,
  MessageSquareText,
  TriangleAlert,
  XCircle,
} from 'lucide-react'

import { evalKeys } from '@/lib/queryKeys'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { cn, titleCase } from '@/lib/utils'
import { useCapability } from '@/features/permissions/hooks'
import { fetchEvalRunAssertionResults, fetchRelayEvalRun } from '../api'
import { SessionConfigSection } from './EvalRunDetailSheet/SessionConfigSection'
import { ToolCallEvents } from './EvalRunDetailSheet/ToolCallEvents'
import {
  formatEvalRunDateTime,
  formatEvalRunStatus,
  getAssertionStats,
  isActiveEvalRun,
} from '../evalRunPresentation'
import type {
  EvalRunAssertionVerdict,
  RelayEvalRun,
  RelayEvalRunAssertionResult,
  RelayEvalRunChatTurn,
  ToolCallEvent,
} from '../types'

interface EvalRunDetailSheetProps {
  run: RelayEvalRun | null
  onOpenChange: (isOpen: boolean) => void
}

interface TranscriptItem {
  id: string
  speaker: string
  content: string
  timeLabel: string | null
  isAgent: boolean
  toolEvents: ToolCallEvent[] | null
}

const assertionStatusClassNames: Record<EvalRunAssertionVerdict, string> = {
  passed: 'bg-success-ui-bg text-success-solid',
  failed: 'bg-error-ui-bg text-error-solid',
  inconclusive: 'bg-warning-ui-bg text-warning-solid',
  error: 'bg-error-ui-bg text-error-solid',
}

const assertionStatusIcons = {
  passed: CheckCircle2,
  failed: XCircle,
  inconclusive: CircleHelp,
  error: TriangleAlert,
} satisfies Record<EvalRunAssertionVerdict, typeof CheckCircle2>

function getAssertionStatusLabel(status: EvalRunAssertionVerdict): string {
  return titleCase(status)
}

function getAssertionName(assertion: RelayEvalRunAssertionResult): string {
  return (
    assertion.assertion?.name?.trim() ||
    `Assertion ${assertion.assertion_id.slice(0, 8)}`
  )
}

function getAssertionCriteria(assertion: RelayEvalRunAssertionResult): string {
  return (
    assertion.assertion?.criteria?.trim() || 'No assertion criteria provided.'
  )
}

function getAssertionError(
  assertion: RelayEvalRunAssertionResult
): string | null {
  return assertion.error_text?.trim() || null
}

function getAssertionReasoning(assertion: RelayEvalRunAssertionResult): string {
  return assertion.reasoning?.trim() || 'No evaluator reasoning available yet.'
}

function getEvidenceSummary(
  assertion: RelayEvalRunAssertionResult
): string | null {
  const first = assertion.evidence_json[0]
  if (!first) return null
  for (const key of ['quote', 'text', 'evidence', 'excerpt']) {
    const value = first[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  try {
    return JSON.stringify(first)
  } catch {
    return null
  }
}

function formatConfidence(value: number | null): string | null {
  if (value === null || Number.isNaN(value)) return null
  return `${Math.round(value * 100)}% confidence`
}

function mapChatTurns(turns: RelayEvalRunChatTurn[]): TranscriptItem[] {
  return turns.map((turn) => {
    const isAgent =
      turn.participant_role === 'duty_agent' ||
      turn.participant_role === 'agent'
    return {
      id: turn.id,
      speaker: isAgent ? 'Agent' : 'Persona',
      content: turn.content,
      timeLabel: null,
      isAgent,
      toolEvents: turn.tool_calls_json ?? null,
    }
  })
}

export function EvalRunDetailSheet({
  run: runFromList,
  onOpenChange,
}: EvalRunDetailSheetProps) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const showRawPayload = useCapability('duty.evals') === true
  const selectedRunId = runFromList?.id ?? null
  const selectedRunDetailQuery = useQuery({
    queryKey: evalKeys.runDetail(organization?.id, selectedRunId),
    enabled: Boolean(selectedRunId),
    queryFn: async () => {
      const runId = selectedRunId
      if (!runId) throw new Error('No eval run selected')
      const token = await getToken()
      if (!token) throw new Error('No authentication token available')
      return fetchRelayEvalRun(token, runId)
    },
    refetchInterval: (query) => {
      const run = query.state.data?.run ?? runFromList
      if (!run || !isActiveEvalRun(run)) return false
      return 3000
    },
    staleTime: 5000,
    refetchOnWindowFocus: false,
  })
  const selectedRun = selectedRunDetailQuery.data?.run ?? runFromList
  const failureReason = selectedRun?.error_text?.trim() || null
  const selectedRunAssertionsQuery = useQuery({
    queryKey: evalKeys.runAssertionResults(organization?.id, selectedRunId),
    enabled: Boolean(selectedRunId),
    queryFn: async () => {
      const runId = selectedRunId
      if (!runId) throw new Error('No eval run selected')
      const token = await getToken()
      if (!token) throw new Error('No authentication token available')
      return fetchEvalRunAssertionResults(token, runId)
    },
    refetchInterval: selectedRun && isActiveEvalRun(selectedRun) ? 3000 : false,
    staleTime: 5000,
    refetchOnWindowFocus: false,
  })
  const selectedRunAssertions = selectedRunAssertionsQuery.data?.items ?? []
  const selectedRunStats = getAssertionStats(selectedRunAssertions)
  const transcriptItems = useMemo(
    () => mapChatTurns(selectedRunDetailQuery.data?.chat_turns ?? []),
    [selectedRunDetailQuery.data?.chat_turns]
  )

  return (
    <Sheet open={Boolean(runFromList)} onOpenChange={onOpenChange}>
      <SheetContent
        className="bg-neutral-ui-bg overflow-y-auto gap-0 p-0 w-full max-w-none sm:max-w-lg rounded-l-xl"
        onOpenAutoFocus={(event) => event.preventDefault()}>
        <SheetHeader className="bg-neutral-ui-bg sticky top-0 z-10 border-b border-gray-100">
          <SheetTitle className="text-left">
            {selectedRun ? `Run ${selectedRun.id}` : 'Run details'}
          </SheetTitle>
          <SheetDescription>
            Run context, transcript, and assertion results.
          </SheetDescription>
        </SheetHeader>

        {selectedRun ? (
          <div className="space-y-4 px-4 py-4">
            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-primary-high-contrast">
                Run metadata
              </h3>
              <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-neutral-low-contrast">Status</dt>
                  <dd className="mt-1 font-medium text-primary-high-contrast">
                    {formatEvalRunStatus(selectedRun.status)}
                  </dd>
                </div>
                <div>
                  <dt className="text-neutral-low-contrast">Started</dt>
                  <dd className="mt-1 font-medium text-primary-high-contrast">
                    {formatEvalRunDateTime(selectedRun.started_at)}
                  </dd>
                </div>
                <div>
                  <dt className="text-neutral-low-contrast">Ended</dt>
                  <dd className="mt-1 font-medium text-primary-high-contrast">
                    {formatEvalRunDateTime(selectedRun.ended_at)}
                  </dd>
                </div>
              </dl>
              {showRawPayload ? (
                <SessionConfigSection run={selectedRun} showRawPayload />
              ) : null}
            </section>

            {failureReason ? (
              <section className="rounded-lg border border-error-ui-border bg-error-ui-bg p-4">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-error-solid" />
                  <h3 className="text-sm font-semibold text-error-solid">
                    Failure reason
                  </h3>
                </div>
                <p className="mt-2 text-sm whitespace-pre-wrap break-words text-error-solid">
                  {failureReason}
                </p>
              </section>
            ) : null}

            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-2">
                <MessageSquareText className="h-4 w-4 text-primary-solid" />
                <h3 className="text-sm font-semibold text-primary-high-contrast">
                  Transcript
                </h3>
              </div>

              {selectedRunDetailQuery.isLoading ? (
                <p className="mt-4 text-sm text-neutral-low-contrast">
                  Loading run context...
                </p>
              ) : null}

              {selectedRunDetailQuery.isError ? (
                <p className="mt-4 text-sm text-error-solid">
                  Unable to load run context.
                </p>
              ) : null}

              {!selectedRunDetailQuery.isLoading &&
              transcriptItems.length > 0 ? (
                <div className="mt-4 space-y-2">
                  {transcriptItems.map((turn) => (
                    <article
                      key={turn.id}
                      className={cn(
                        'rounded-lg border px-3 py-2.5',
                        turn.isAgent
                          ? 'border-primary-ui-border bg-primary-ui-bg'
                          : 'border-gray-100 bg-gray-50'
                      )}>
                      <div className="flex items-center justify-between gap-3 text-xs">
                        <span className="font-semibold text-primary-high-contrast">
                          {turn.speaker}
                        </span>
                        {turn.timeLabel ? (
                          <span className="font-mono text-neutral-low-contrast">
                            {turn.timeLabel}
                          </span>
                        ) : null}
                      </div>
                      {turn.toolEvents && turn.toolEvents.length > 0 ? (
                        <ToolCallEvents events={turn.toolEvents} />
                      ) : null}
                      <p className="mt-1 text-sm leading-6 text-primary-high-contrast">
                        {turn.content}
                      </p>
                    </article>
                  ))}
                </div>
              ) : null}

              {!selectedRunDetailQuery.isLoading &&
              transcriptItems.length === 0 &&
              !selectedRunDetailQuery.isError ? (
                <p className="mt-4 text-sm text-neutral-low-contrast">
                  No transcript available for this run yet.
                </p>
              ) : null}
            </section>

            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-primary-high-contrast">
                  Assertion results
                </h3>
                <div className="flex items-center gap-2 text-xs">
                  <span className="rounded-full bg-gray-100 px-2.5 py-1 font-semibold text-neutral-low-contrast">
                    {selectedRunStats.total} total
                  </span>
                  <span className="rounded-full bg-success-ui-bg px-2.5 py-1 font-semibold text-success-solid">
                    {selectedRunStats.passed} passed
                  </span>
                  {selectedRunStats.failed > 0 ? (
                    <span className="rounded-full bg-error-ui-bg px-2.5 py-1 font-semibold text-error-solid">
                      {selectedRunStats.failed} failed
                    </span>
                  ) : null}
                </div>
              </div>

              {selectedRunAssertionsQuery.isLoading ? (
                <p className="mt-4 text-sm text-neutral-low-contrast">
                  Loading assertion results...
                </p>
              ) : null}

              {selectedRunAssertionsQuery.isError ? (
                <p className="mt-4 text-sm text-error-solid">
                  Unable to load assertion results.
                </p>
              ) : null}

              {!selectedRunAssertionsQuery.isLoading &&
              !selectedRunAssertionsQuery.isError &&
              selectedRunAssertions.length === 0 ? (
                <p className="mt-4 text-sm text-neutral-low-contrast">
                  No assertion results available for this run yet.
                </p>
              ) : null}

              {selectedRunAssertions.length > 0 ? (
                <div className="mt-4 divide-y divide-gray-100">
                  {selectedRunAssertions.map((assertion) => {
                    const evidence = getEvidenceSummary(assertion)
                    const confidence = formatConfidence(assertion.confidence)
                    const status = assertion.verdict
                    const AssertionStatusIcon = assertionStatusIcons[status]
                    const assertionError = getAssertionError(assertion)

                    return (
                      <article
                        key={assertion.id}
                        className="py-4 first:pt-0 last:pb-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h4 className="text-sm font-semibold text-primary-high-contrast">
                              {getAssertionName(assertion)}
                            </h4>
                            <p className="mt-1 text-sm text-neutral-low-contrast">
                              {getAssertionCriteria(assertion)}
                            </p>
                          </div>
                          <span
                            className={cn(
                              'inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold',
                              assertionStatusClassNames[status]
                            )}>
                            <AssertionStatusIcon className="h-3.5 w-3.5" />
                            {getAssertionStatusLabel(status)}
                          </span>
                        </div>
                        {assertionError ? (
                          <div className="mt-3 rounded-md border border-error-ui-border bg-error-ui-bg px-3 py-2 text-sm text-error-solid">
                            <span className="font-semibold">
                              Evaluation error:{' '}
                            </span>
                            <span className="whitespace-pre-wrap break-words">
                              {assertionError}
                            </span>
                          </div>
                        ) : (
                          <div className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-sm text-neutral-low-contrast">
                            {getAssertionReasoning(assertion)}
                          </div>
                        )}
                        {evidence ? (
                          <p className="mt-2 text-xs leading-5 text-neutral-low-contrast">
                            Evidence: {evidence}
                          </p>
                        ) : null}
                        {confidence ? (
                          <p className="mt-1 text-xs text-neutral-low-contrast">
                            {confidence}
                          </p>
                        ) : null}
                      </article>
                    )
                  })}
                </div>
              ) : null}
            </section>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
